import React, { useMemo, useState, useEffect } from 'react';
import { Card, Row, Col, Space, Typography, Button, Form, Input, Select, DatePicker, InputNumber, Tooltip, Alert, Modal, Checkbox, Switch, Tag, Table, message } from 'antd';
import { DeleteOutlined, InfoCircleOutlined, ExclamationCircleOutlined, CopyOutlined, GiftOutlined, PlusOutlined } from '@ant-design/icons';
import { validateCampaignDates, validateLineItemBudgets, detectFlightOverlaps } from '../utils/campaignValidation';
import { usePermify } from '@permify/react-role';
import { useCampaignRequestorContext } from '../../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { getCpmForCampaignType, isPercentageModel, getMinimumCpmForCampaignType, getPercentageForAdvertiser, getMinimumPercentageForAdvertiser, validatePercentageOverride } from '../../../../core/utils/pricing/cpmHelper';
import { getPricingFieldsForSave } from '../../../../core/utils/pricing/pricingFieldMapper';
import moment from 'moment-timezone';
import { useTimezone } from '../../../../core/hooks/useTimezone';

const { Text } = Typography;
const { confirm } = Modal;

// Wrapper that converts Form.Item's injected UTC value to user timezone for display
const TimezoneDatePicker = ({ value, onChange, timezone, ...props }) => {
  const displayValue = value ? moment(value).tz(timezone) : null;
  return <DatePicker {...props} value={displayValue} onChange={onChange} />;
};

/**
 * CampaignHeader Component
 *
 * Displays and manages campaign-level configuration within a campaign order.
 * Each campaign has:
 * - Type (Display, Video, External) - required
 * - Name (required)
 * - Budget allocation (required)
 * - Optional date range override (defaults to order dates)
 * - Clone action (creates a duplicate campaign)
 * - Delete action (disabled if only 1 campaign exists)
 *
 * Props:
 * @param {number} campaignIndex - Index of the campaign in the campaigns array
 * @param {Object} form - Ant Design form instance
 * @param {number} orderBudget - Total order budget for validation
 * @param {Object} orderDates - { startDate, endDate } from order level
 * @param {Function} onClone - Callback when clone button clicked
 * @param {Function} onDelete - Callback when delete button clicked
 * @param {number} campaignCount - Total number of campaigns (for delete constraint)
 * @param {Array} campaigns - All campaigns for budget calculations
 * @param {boolean} isAdvancedMode - Whether advanced mode is enabled
 * @param {boolean} showOnlyDeliveryItems - Filter state to show only line items marked for delivery
 * @param {Function} onDeliveryFilterChange - Callback when delivery filter toggle changes
 * @param {Object} helpers - Helper functions from useCampaignForm (includes isCloning)
 */
const CampaignHeader = React.memo(({
  campaignIndex,
  form,
  orderBudget,
  orderDates,
  onClone,
  onDelete,
  campaignCount,
  campaigns = [],
  isAdvancedMode = false,
  showOnlyDeliveryItems = false,
  onDeliveryFilterChange,
  helpers,
  hasTraffickerOrProcessorPermission = false,
  isNotDraftStatus = false,
}) => {
  // Get selected advertiser and campaign order from context for pricing model
  const { selectedAdvertiser, currentCampaignOrder } = useCampaignRequestorContext();
  const { timezone } = useTimezone();

  // Get current campaign data from form
  const campaignType = Form.useWatch(['campaigns', campaignIndex, 'type'], form);
  const campaignName = Form.useWatch(['campaigns', campaignIndex, 'name'], form);
  const campaignBudget = Form.useWatch(['campaigns', campaignIndex, 'budget'], form) || 0;
  const campaignCpm = Form.useWatch(['campaigns', campaignIndex, 'cpm'], form);
  const campaignStartDate = Form.useWatch(['campaigns', campaignIndex, 'startDate'], form);
  const campaignEndDate = Form.useWatch(['campaigns', campaignIndex, 'endDate'], form);
  const lineItems = Form.useWatch(['campaigns', campaignIndex, 'lineItems'], form) || [];
  const hasMediaPlanStrategy = Form.useWatch(['campaigns', campaignIndex, 'hasMediaPlanStrategy'], form);
  const isLaunchProgrammatically = Form.useWatch(['campaigns', campaignIndex, 'isLaunchProgrammatically'], form);

  // Flight schedule state for campaign-level flights
  const campaign = campaigns[campaignIndex] || {};

  // Get campaign status from the current campaign in the form
  const campaignStatus = campaign?.status;
  const campaignValueAdd = Form.useWatch(['campaigns', campaignIndex, 'valueAdd'], form);
  const campaignDsp = Form.useWatch(['campaigns', campaignIndex, 'demandSidePlatform'], form);
  const [flightSchedule, setFlightSchedule] = useState(campaign.flightSchedules || campaign.flightSchedule || []);
  const [hasInitializedFlightSchedule, setHasInitializedFlightSchedule] = useState(false);

  // Initialize flight schedule from existing campaign data ONCE only
  useEffect(() => {
    const existingFlights = campaign.flightSchedules || campaign.flightSchedule || [];
    if (existingFlights.length > 0 && !hasInitializedFlightSchedule) {
      setFlightSchedule(existingFlights);
      setHasInitializedFlightSchedule(true);
    }
  }, [campaign.flightSchedules, campaign.flightSchedule, hasInitializedFlightSchedule]);

  // Sync flight schedule state to form values whenever it changes
  useEffect(() => {
    const currentFormFlightSchedule = campaigns[campaignIndex]?.flightSchedule || [];

    // Simple length check + JSON comparison to detect changes
    const hasChanged = JSON.stringify(currentFormFlightSchedule) !== JSON.stringify(flightSchedule);

    if (hasChanged) {
      const fullFormValues = form.getFieldsValue();
      const allCampaigns = fullFormValues.campaigns || [];

      const updatedAllCampaigns = allCampaigns.map((c, cIdx) => {
        if (cIdx === campaignIndex) {
          return {
            ...c,
            flightSchedule: flightSchedule,
          };
        }
        return c;
      });

      form.setFieldsValue({ campaigns: updatedAllCampaigns });

      // Trigger form validation to update validationState immediately
      form.validateFields().catch(() => {});
    }
  }, [flightSchedule, form, campaignIndex, campaigns]);

  /**
   * Auto-adjust campaign order end date when flight schedule end date exceeds it
   */
  const autoAdjustEndDatesFromFlightSchedule = (updatedFlightSchedule) => {
    if (!updatedFlightSchedule || updatedFlightSchedule.length === 0) return;

    // Find the furthest end date among all flight schedules
    let furthestEndDate = null;
    updatedFlightSchedule.forEach((flight) => {
      if (flight.endDate) {
        const flightEnd = moment(flight.endDate);
        if (!furthestEndDate || flightEnd.isAfter(furthestEndDate)) {
          furthestEndDate = flightEnd;
        }
      }
    });

    if (!furthestEndDate) return;

    const fullFormValues = form.getFieldsValue();
    const fullFormUpdates = {};

    // Check if campaign order end date needs adjustment
    const currentOrderEnd = fullFormValues.endDate ? moment(fullFormValues.endDate) : null;
    let needsOrderUpdate = false;
    if (!currentOrderEnd || furthestEndDate.isAfter(currentOrderEnd)) {
      needsOrderUpdate = true;
      fullFormUpdates.endDate = furthestEndDate;
    }

    // Check if campaign end date needs adjustment
    const allCampaigns = fullFormValues.campaigns || [];
    const currentCampaign = allCampaigns[campaignIndex];
    const currentCampaignEnd = currentCampaign?.endDate ? moment(currentCampaign.endDate) : null;
    let needsCampaignUpdate = false;
    if (!currentCampaignEnd || furthestEndDate.isAfter(currentCampaignEnd)) {
      needsCampaignUpdate = true;
      const updatedAllCampaigns = allCampaigns.map((c, cIdx) => {
        if (cIdx === campaignIndex) {
          return { ...c, endDate: furthestEndDate };
        }
        return c;
      });
      fullFormUpdates.campaigns = updatedAllCampaigns;
    }

    if (Object.keys(fullFormUpdates).length > 0) {
      form.setFieldsValue(fullFormUpdates);

      const adjustedItems = [];
      if (needsCampaignUpdate) adjustedItems.push('campaign');
      if (needsOrderUpdate) adjustedItems.push('campaign order');

      message.info(
        `End date auto-adjusted for ${adjustedItems.join(', ')} to match flight schedule end date (${furthestEndDate.format('MM/DD/YYYY')})`,
        4
      );
    }
  };

  // Check if pricing model is Percentage (hide CPM fields)
  // For existing campaigns, use the saved pricingModel; for new campaigns, use advertiser's pricing model
  const savedPricingModel = currentCampaignOrder?.pricingModel;
  const hideCpmFields = useMemo(() => {
    if (savedPricingModel) {
      return savedPricingModel === 'Percentage';
    }
    return isPercentageModel(selectedAdvertiser);
  }, [savedPricingModel, selectedAdvertiser]);

  // Determine pricing model for display in Advanced Mode
  const pricingModel = useMemo(() => {
    // Priority 1: Use pricingModel directly from campaignOrder (string value)
    let model = currentCampaignOrder?.pricingModel;

    // Priority 2: Try selectedAdvertiser.pricingModel.model
    if (!model && selectedAdvertiser?.pricingModel?.model) {
      model = selectedAdvertiser.pricingModel.model;
    }

    // Priority 3: Try currentCampaignOrder.advertiser.pricingModel.model
    if (!model && currentCampaignOrder?.advertiser?.pricingModel?.model) {
      model = currentCampaignOrder.advertiser.pricingModel.model;
    }

    if (!model) {
      return null;
    }

    if (model === 'Percentage') {
      return {
        name: 'Percentage',
        description: 'Budget-based pricing',
        color: 'purple',
        tooltip: 'This campaign uses percentage-based pricing where costs are calculated as a percentage of the budget allocation.'
      };
    }

    if (model === 'CPM') {
      return {
        name: 'CPM',
        description: 'Impression-based pricing',
        color: 'blue',
        tooltip: 'This campaign uses CPM (Cost Per Mille) pricing where costs are based on impressions delivered.'
      };
    }

    return null;
  }, [selectedAdvertiser, currentCampaignOrder]);

  // Get campaign order name for checkbox functionality
  const campaignOrderName = Form.useWatch('name', form);

  // Track previous campaign type to detect changes
  const [previousType, setPreviousType] = React.useState(campaignType);

  // Track if CPM has been initialized to prevent auto-fill when user clears field
  const cpmInitialized = React.useRef(false);

  // State for "Same as campaign order name" checkbox
  const [useCampaignOrderName, setUseCampaignOrderName] = useState(true);

  // Permission checking for budget warning visibility
  const { isAuthorized } = usePermify();
  const [showBudgetWarning, setShowBudgetWarning] = useState(false);
  const [canEditCpm, setCanEditCpm] = useState(false);

  // Check if user is Ad Ops (only Ad Ops should see budget warning)
  useEffect(() => {
    const checkPermissions = async () => {
      const hasAdOpsPermission = await isAuthorized(['isTrafficker', 'isProcessor'], []);
      setShowBudgetWarning(hasAdOpsPermission);
    };

    checkPermissions();
  }, [isAuthorized]);

  // Check if user can edit CPM fields (only Ad Ops)
  useEffect(() => {
    const checkCpmPermissions = async () => {
      const hasAdOpsPermission = await isAuthorized(['isTrafficker', 'isProcessor'], []);
      setCanEditCpm(hasAdOpsPermission);
    };
    checkCpmPermissions();
  }, [isAuthorized]);

  // Auto-populate CPM from advertiser when creating a new campaign order
  useEffect(() => {
    // Only auto-populate if:
    // 1. We have a selected advertiser and campaign type
    // 2. Current campaign order is not set (new campaign order)
    // 3. CPM has not been initialized yet (first time only)
    if (!selectedAdvertiser || !campaignType || currentCampaignOrder?.id) {
      return;
    }

    // Skip if we've already initialized CPM - don't auto-fill when user clears
    if (cpmInitialized.current) {
      return;
    }

    if (campaignCpm && campaignCpm > 0) {
      // CPM already has a value, mark as initialized
      cpmInitialized.current = true;
      return;
    }

    // Get CPM from advertiser based on campaign type
    const advertiserCpm = getCpmForCampaignType(selectedAdvertiser, campaignType);
    const pricingFields = getPricingFieldsForSave(advertiserCpm, selectedAdvertiser, campaignType);

    // Set the CPM value in the form
    if (pricingFields.cpm !== campaignCpm) {
      form.setFieldValue(['campaigns', campaignIndex, 'cpm'], pricingFields.cpm);

      // Also set contractCPM if it exists
      if (pricingFields.contractCPM) {
        form.setFieldValue(['campaigns', campaignIndex, 'contractCPM'], pricingFields.contractCPM);
      }

      // Set percentage fields if applicable
      if (pricingFields.advertiserPercentage !== null) {
        form.setFieldValue(['campaigns', campaignIndex, 'advertiserPercentage'], pricingFields.advertiserPercentage);
      }
      if (pricingFields.contractPercentage !== null) {
        form.setFieldValue(['campaigns', campaignIndex, 'contractPercentage'], pricingFields.contractPercentage);
      }

      // Mark as initialized after setting
      cpmInitialized.current = true;
    }
  }, [selectedAdvertiser, campaignType, campaignCpm, currentCampaignOrder, form, campaignIndex, hideCpmFields]);

  // NOTE: Percentage auto-population is now handled in CampaignRequestorEdit.js
  // when the advertiser changes, along with CPM updates. This ensures consistent
  // batch updates for all pricing fields (CPM, contractCPM, advertiserPercentage, contractPercentage)

  // Auto-update campaign name when campaign order name changes (if checkbox is checked)
  // Debounced to avoid updating on every keystroke
  useEffect(() => {
    if (!useCampaignOrderName || !campaignOrderName) {
      return;
    }

    // Debounce the update by 500ms
    const timeoutId = setTimeout(() => {
      const currentValueAdd = form.getFieldValue(['campaigns', campaignIndex, 'valueAdd']);
      const nameWithSuffix = currentValueAdd && !campaignOrderName.endsWith('-$')
        ? `${campaignOrderName}-$`
        : campaignOrderName;

      form.setFieldValue(['campaigns', campaignIndex, 'name'], nameWithSuffix);
      // Trigger validation to clear any errors
      setTimeout(() => {
        form.validateFields([['campaigns', campaignIndex, 'name']]).catch(() => {
          // Ignore validation errors
        });
      }, 0);
    }, 500);

    // Cleanup: cancel the timeout if campaignOrderName changes again before 500ms
    return () => clearTimeout(timeoutId);
  }, [campaignOrderName, useCampaignOrderName, form, campaignIndex]);

  // Calculate validation states
  const dateValidation = useMemo(() => {
    if (!orderDates.startDate || !orderDates.endDate) {
      return { valid: true, error: null };
    }

    return validateCampaignDates(
      {
        startDate: campaignStartDate,
        endDate: campaignEndDate,
      },
      orderDates.startDate,
      orderDates.endDate
    );
  }, [campaignStartDate, campaignEndDate, orderDates]);

  const budgetValidation = useMemo(() => {
    // Check if at least one line item exists (regardless of delivery status)
    if (!lineItems || lineItems.length === 0) {
      return {
        valid: false,
        error: 'At least one line item is required',
        warning: null,
      };
    }

    // Only validate budgets for line items marked for delivery
    // In advanced mode: only count items explicitly marked for delivery (markedForDelivery === true)
    // In normal mode: count all items except those explicitly excluded (markedForDelivery !== false)
    const deliveryLineItems = isAdvancedMode
      ? lineItems.filter(item => item.markedForDelivery === true)
      : lineItems.filter(item => item.markedForDelivery !== false);

    // If no line items marked for delivery, budgets are valid (0 allocation is okay)
    if (deliveryLineItems.length === 0) {
      return {
        valid: true,
        error: null,
        warning: 'No line items marked for delivery',
        totalAllocated: 0,
        utilization: 0,
        unallocated: campaignBudget,
      };
    }

    return validateLineItemBudgets(deliveryLineItems, campaignBudget);
  }, [lineItems, campaignBudget, isAdvancedMode]);

  // Calculate budget utilization
  const budgetUtilization = useMemo(() => {
    if (!campaignBudget || campaignBudget === 0) return 0;

    // Only include line items marked for delivery
    // In advanced mode: only count items explicitly marked for delivery (markedForDelivery === true)
    // In normal mode: count all items except those explicitly excluded (markedForDelivery !== false)
    const deliveryLineItems = isAdvancedMode
      ? lineItems.filter(item => item.markedForDelivery === true)
      : lineItems.filter(item => item.markedForDelivery !== false);
    const totalLineItemBudget = deliveryLineItems.reduce(
      (sum, item) => sum + (parseFloat(item.budgetTotal) || 0),
      0
    );

    return Math.round((totalLineItemBudget / campaignBudget) * 100);
  }, [lineItems, campaignBudget, isAdvancedMode]);

  // Detect flight schedule overlaps
  const flightOverlaps = useMemo(() => {
    return detectFlightOverlaps(flightSchedule);
  }, [flightSchedule]);

  // Calculate remaining budget across all campaigns
  const remainingOrderBudget = useMemo(() => {
    const totalCampaignBudgets = campaigns.reduce(
      (sum, campaign) => sum + (parseFloat(campaign.budget) || 0),
      0
    );
    return orderBudget - totalCampaignBudgets;
  }, [campaigns, orderBudget]);

  // Calculate impressions based on budget and CPM
  const campaignImpressions = useMemo(() => {
    if (!campaignBudget || !campaignCpm) return 0;
    return Math.ceil((Number(campaignBudget) / Number(campaignCpm)) * 1000);
  }, [campaignBudget, campaignCpm]);

  const campaignTypeLabels = {
    1: 'Display',
    7: 'External',
    8: 'Video',
  };

  // Only allow deletion if:
  // 1. There are multiple campaigns (must have at least 1 campaign)
  // 2. AND it's not the first campaign (Campaign 1 at index 0 cannot be deleted)
  const canDelete = campaignCount > 1 && campaignIndex > 0;

  // Handle clone
  const handleCloneClick = () => {
    if (onClone) {
      onClone(campaignIndex);
    }
  };

  // Handle campaign type change with confirmation
  const handleCampaignTypeChange = (newType) => {
    // CRITICAL: Don't clear ad groups during cloning initialization
    // When cloning, form.setFieldsValue triggers this onChange handler
    // but we want to preserve the cloned ad groups
    if (helpers?.isCloning && helpers.isCloning()) {
      // During clone, just update the previousType tracker
      // The form values are already being set by handleInitialValues
      setPreviousType(newType);
      return;
    }

    // Helper function to update type and CPM together
    const updateTypeAndCpm = () => {
      // Get new CPM for display (used for line items which don't store contractCPM)
      const newCampaignCpm = selectedAdvertiser
        ? getCpmForCampaignType(selectedAdvertiser, newType)
        : 7;

      // Get pricing fields (both cpm AND contractCPM) for the campaign
      const pricingFields = selectedAdvertiser
        ? getPricingFieldsForSave(newCampaignCpm, selectedAdvertiser, newType)
        : { cpm: 7, contractCPM: 7, advertiserPercentage: null, contractPercentage: null };

      // Get all campaigns from form
      const allCampaigns = form.getFieldValue('campaigns') || [];

      // Update the campaign and its line items
      const updatedCampaigns = allCampaigns.map((campaign, idx) => {
        if (idx === campaignIndex) {
          return {
            ...campaign,
            type: newType,
            // Campaign level: set both cpm and contractCPM
            cpm: pricingFields.cpm,
            contractCPM: pricingFields.contractCPM,
            advertiserPercentage: pricingFields.advertiserPercentage,
            contractPercentage: pricingFields.contractPercentage,
            lineItems: (campaign.lineItems || []).map(lineItem => ({
              ...lineItem,
              cpm: newCampaignCpm, // Line items only have cpm field (not contractCPM)
              adGroup: null, // Clear entire ad group (ID, clickthrough, and all creatives)
            }))
          };
        }
        return campaign;
      });

      // Update form with new values
      form.setFieldsValue({ campaigns: updatedCampaigns });
      setPreviousType(newType);

      // Trigger validation to show errors immediately
      setTimeout(() => {
        form.validateFields([
          ['campaigns', campaignIndex, 'cpm'],
        ]).catch(() => {
          // Ignore validation errors - just showing them to user
        });
      }, 0);
    };

    // Check if there are any creatives in line items
    const hasCreatives = lineItems.some(lineItem =>
      lineItem.adGroup?.adGroupCreatives &&
      lineItem.adGroup.adGroupCreatives.length > 0
    );

    // If no creatives exist, just update type and CPM
    if (!hasCreatives) {
      updateTypeAndCpm();
      return;
    }

    // Show confirmation if creatives exist
    confirm({
      title: 'Change Campaign Type?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Changing the campaign type will remove all uploaded creatives from line items in this campaign.</p>
          <p><strong>This action cannot be undone.</strong></p>
          <p>Do you want to continue?</p>
        </div>
      ),
      okText: 'Yes, Change Type',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        updateTypeAndCpm();
      },
      onCancel() {
        // Revert to previous type if cancelled
        form.setFieldValue(['campaigns', campaignIndex, 'type'], previousType);
      },
    });
  };

  // Handle delete with confirmation
  const handleDeleteClick = () => {
    confirm({
      title: 'Delete Campaign?',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete Campaign ${campaignIndex + 1}?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk() {
        onDelete(campaignIndex);
      },
    });
  };

  // Handle "Same as campaign order name" checkbox change
  const handleCampaignOrderNameCheckbox = (e) => {
    const checked = e.target.checked;
    setUseCampaignOrderName(checked);

    if (checked && campaignOrderName) {
      // Copy campaign order name to campaign name, adding $ suffix if campaign is value add
      const currentValueAdd = form.getFieldValue(['campaigns', campaignIndex, 'valueAdd']);
      const nameWithSuffix = currentValueAdd && !campaignOrderName.endsWith('-$')
        ? `${campaignOrderName}-$`
        : campaignOrderName;

      form.setFieldValue(['campaigns', campaignIndex, 'name'], nameWithSuffix);
      // Clear validation error by validating the field after setting the value
      setTimeout(() => {
        form.validateFields([['campaigns', campaignIndex, 'name']]).catch(() => {
          // Ignore validation errors
        });
      }, 0);
    } else if (!checked) {
      // Clear campaign name when unchecked
      form.setFieldValue(['campaigns', campaignIndex, 'name'], '');
    }
  };

  // Handle campaign value add toggle with cascading to all line items
  const handleCampaignValueAdd = () => {
    const currentValueAdd = form.getFieldValue(['campaigns', campaignIndex, 'valueAdd']);
    const newValueAdd = !currentValueAdd;

    // Get current campaign and all its line items
    const allCampaigns = form.getFieldValue('campaigns') || [];
    const updatedCampaigns = allCampaigns.map((campaign, idx) => {
      if (idx === campaignIndex) {
        // Update campaign name with $ suffix
        let updatedCampaignName = campaign.name ? campaign.name.trim() : '';

        if (newValueAdd) {
          // Toggle ON: Add -$ to campaign name
          if (!updatedCampaignName.endsWith('-$')) {
            updatedCampaignName = updatedCampaignName + '-$';
          }
        } else {
          // Toggle OFF: Remove -$ from campaign name
          if (updatedCampaignName.endsWith('-$')) {
            updatedCampaignName = updatedCampaignName.slice(0, -2).trim();
          }
        }

        // Cascade to all line items (except origin line items)
        const updatedLineItems = (campaign.lineItems || []).map(lineItem => {
          // Skip origin line items - they cannot be marked as value add
          if (lineItem.isOrigin) {
            return lineItem;
          }

          let updatedLineItemName = lineItem.name ? lineItem.name.trim() : '';

          if (newValueAdd) {
            // Toggle ON: Add -$ to line item name
            if (!updatedLineItemName.endsWith('-$')) {
              updatedLineItemName = updatedLineItemName + '-$';
            }
          } else {
            // Toggle OFF: Remove -$ from line item name
            if (updatedLineItemName.endsWith('-$')) {
              updatedLineItemName = updatedLineItemName.slice(0, -2).trim();
            }
          }

          return {
            ...lineItem,
            name: updatedLineItemName,
            valueAdd: newValueAdd,
          };
        });

        return {
          ...campaign,
          name: updatedCampaignName,
          valueAdd: newValueAdd,
          lineItems: updatedLineItems,
        };
      }
      return campaign;
    });

    form.setFieldsValue({ campaigns: updatedCampaigns });

    // Trigger validation to update form state
    setTimeout(() => {
      form.validateFields().catch(() => {
        // Ignore validation errors
      });
    }, 0);
  };

  return (
    <Card
      style={{
        marginBottom: 16,
        border: '2px solid #e8e8e8',
        borderRadius: '8px',
      }}
      bodyStyle={{ padding: '16px' }}
    >
      <Row gutter={[16, 16]}>
        {/* Campaign Header Row */}
        <Col span={24}>
          <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'nowrap' }}>
            <Space style={{ whiteSpace: 'nowrap', flexShrink: 1, minWidth: 0 }}>
              <Text strong style={{ fontSize: 16, whiteSpace: 'nowrap' }}>
                Campaign {campaignIndex + 1}
              </Text>
              {campaignType && (
                <Text type="secondary" style={{ fontSize: 14 }}>
                  ({campaignTypeLabels[campaignType]})
                </Text>
              )}
              {/* Value Add Tag */}
              {campaignValueAdd && (
                <Tag icon={<GiftOutlined />} color="purple">
                  Value Add
                </Tag>
              )}
              {/* Pricing Model Indicator - Advanced Mode Only */}
              {isAdvancedMode && pricingModel && (
                <Tooltip title={pricingModel.tooltip}>
                  <Tag color={pricingModel.color} style={{ marginLeft: 8 }}>
                    {pricingModel.name}: {pricingModel.description}
                  </Tag>
                </Tooltip>
              )}
              {/* Launch Type Indicator - Advanced Mode Only, shown for specific statuses */}
              {isAdvancedMode &&
               isLaunchProgrammatically !== undefined &&
               campaignStatus &&
               ['LIVE', 'LIVE_PENDING', 'LIVE_APPROVED', 'PAUSED'].includes(campaignStatus) && (
                <Tooltip title={
                  isLaunchProgrammatically
                    ? "Campaign is launched programmatically via DSP integration"
                    : "Campaign is launched manually by operations team"
                }>
                  <Tag
                    color={isLaunchProgrammatically ? "purple" : "default"}
                    style={{ marginLeft: 8 }}
                  >
                    {isLaunchProgrammatically ? "Programmatic Launch" : "Manual Launch"}
                  </Tag>
                </Tooltip>
              )}
            </Space>
            <Space style={{ flexShrink: 0 }}>
              {onClone && (
                <Tooltip title="Clone Campaign">
                  <Button
                    shape="circle"
                    type="default"
                    icon={<CopyOutlined />}
                    onClick={handleCloneClick}
                  />
                </Tooltip>
              )}
              {/* Value Add button - visible when user has permission and status is not DRAFT */}
              {hasTraffickerOrProcessorPermission && isNotDraftStatus && (
                <Tooltip title={campaignValueAdd ? 'Marked as Value Add Campaign' : 'Mark as Value Add Campaign'}>
                  <Button
                    shape="circle"
                    icon={<GiftOutlined />}
                    style={{
                      backgroundColor: campaignValueAdd ? '#722ed1' : 'transparent',
                      borderColor: campaignValueAdd ? '#722ed1' : '#d9d9d9',
                      color: campaignValueAdd ? '#fff' : '#722ed1',
                    }}
                    onClick={handleCampaignValueAdd}
                  />
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete Campaign">
                  <Button
                    shape="circle"
                    type="default"
                    icon={<DeleteOutlined />}
                    onClick={handleDeleteClick}
                  />
                </Tooltip>
              )}
            </Space>
          </div>
        </Col>

        {/* Campaign Configuration Row 1: Type and Name */}
        <Col span={24}>
          <Row gutter={16}>
            {/* Campaign Type */}
            <Col span={6}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text strong>Campaign Type *</Text>
                <Form.Item
                  name={[campaignIndex, 'type']}
                  fieldKey={[campaignIndex, 'type']}
                  rules={[
                    { required: true, message: 'Campaign type is required' },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    style={{ width: '100%' }}
                    showSearch
                    placeholder="Select type"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                    onChange={handleCampaignTypeChange}
                    options={[
                      { label: 'Display', value: 1, key: 1 },
                      { label: 'External', value: 7, key: 7 },
                      { label: 'Video', value: 8, key: 8 },
                    ]}
                  />
                </Form.Item>
              </Space>
            </Col>

            {/* Campaign Name */}
            <Col span={18}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Text strong>Campaign Name *</Text>
                <Form.Item
                  name={[campaignIndex, 'name']}
                  fieldKey={[campaignIndex, 'name']}
                  rules={[
                    { required: true, message: 'Campaign name is required' },
                    { max: 100, message: 'Campaign name must be 100 characters or less' },
                  ]}
                  style={{ marginBottom: 8 }}
                >
                  <Input
                    placeholder={`Campaign ${campaignIndex + 1}`}
                    maxLength={100}
                    disabled={useCampaignOrderName}
                    onChange={(e) => {
                      // Strip $ symbols - they should only be added programmatically
                      const cleanValue = e.target.value.replace(/\$/g, '');
                      if (cleanValue !== e.target.value) {
                        form.setFieldValue(['campaigns', campaignIndex, 'name'], cleanValue);
                      }
                    }}
                    onBlur={() => {
                      // Re-add $ to Value Add campaigns if it was removed during editing
                      const currentValueAdd = form.getFieldValue(['campaigns', campaignIndex, 'valueAdd']);
                      if (currentValueAdd) {
                        const currentName = form.getFieldValue(['campaigns', campaignIndex, 'name'])?.trim() || '';
                        if (!currentName.endsWith('-$')) {
                          form.setFieldValue(['campaigns', campaignIndex, 'name'], currentName + '-$');
                        }
                      }
                    }}
                  />
                </Form.Item>
                <Checkbox
                  checked={useCampaignOrderName}
                  onChange={handleCampaignOrderNameCheckbox}
                  disabled={!campaignOrderName}
                >
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Same as campaign order name
                  </Text>
                </Checkbox>
              </Space>
            </Col>
          </Row>
        </Col>

        {/* Campaign Configuration Row 2: Budget and Dates */}
        <Col span={24}>
          <Row gutter={16}>
            {/* Campaign Budget */}
            <Col span={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space>
                  <Text strong>Budget *</Text>
                  <Tooltip title="Budget allocated to this campaign. Line items within this campaign must not exceed this amount.">
                    <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                  </Tooltip>
                </Space>
                <Form.Item
                  name={[campaignIndex, 'budget']}
                  fieldKey={[campaignIndex, 'budget']}
                  rules={[
                    { required: true, message: 'Budget is required' },
                    {
                      validator: async (_, value) => {
                        if (value && value < 0) {
                          throw new Error('Budget must be positive');
                        }
                        if (value && remainingOrderBudget < 0 && campaigns[campaignIndex]?.budget !== value) {
                          throw new Error('Total campaign budgets exceed order budget');
                        }
                      },
                    },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <InputNumber
                    style={{ width: '100%' }}
                    placeholder="0.00"
                    prefix="$"
                    min={0}
                    precision={2}
                    formatter={value => value ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
                {campaignBudget > 0 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {budgetUtilization}% allocated to line items
                  </Text>
                )}
              </Space>
            </Col>

            {/* Campaign Start Date (Optional) */}
            <Col span={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space>
                  <Text strong>Start Date</Text>
                  <Tooltip title="Optional. Leave empty to inherit from order start date.">
                    <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                  </Tooltip>
                </Space>
                <Form.Item
                  name={[campaignIndex, 'startDate']}
                  fieldKey={[campaignIndex, 'startDate']}
                  style={{ marginBottom: 0 }}
                >
                  <TimezoneDatePicker
                    timezone={timezone}
                    style={{ width: '100%' }}
                    placeholder="Order start"
                    format="MM/DD/YYYY"
                    onChange={(date) => {
                      // Normalize to start of day in user timezone, then convert to UTC
                      const normalizedDate = date ? moment.tz(date.format('YYYY-MM-DD'), timezone).startOf('day').utc() : null;
                      form.setFieldValue(['campaigns', campaignIndex, 'startDate'], normalizedDate);
                    }}
                    disabledDate={(current) => {
                      if (!orderDates.startDate || !orderDates.endDate) return false;
                      return current < moment(orderDates.startDate).tz(timezone).startOf('day') ||
                             current > moment(orderDates.endDate).tz(timezone).endOf('day');
                    }}
                  />
                </Form.Item>
              </Space>
            </Col>

            {/* Campaign End Date (Optional) */}
            <Col span={8}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space>
                  <Text strong>End Date</Text>
                  <Tooltip title="Optional. Leave empty to inherit from order end date.">
                    <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                  </Tooltip>
                </Space>
                <Form.Item
                  name={[campaignIndex, 'endDate']}
                  fieldKey={[campaignIndex, 'endDate']}
                  style={{ marginBottom: 0 }}
                >
                  <TimezoneDatePicker
                    timezone={timezone}
                    style={{ width: '100%' }}
                    placeholder="Order end"
                    format="MM/DD/YYYY"
                    onChange={(date) => {
                      // Normalize to end of day in user timezone, then convert to UTC
                      const normalizedDate = date ? moment.tz(date.format('YYYY-MM-DD'), timezone).endOf('day').utc() : null;
                      form.setFieldValue(['campaigns', campaignIndex, 'endDate'], normalizedDate);
                    }}
                    disabledDate={(current) => {
                      if (!orderDates.startDate || !orderDates.endDate) return false;
                      const startDate = campaignStartDate || orderDates.startDate;
                      return current < moment(startDate).tz(timezone).startOf('day') ||
                             current > moment(orderDates.endDate).tz(timezone).endOf('day');
                    }}
                  />
                </Form.Item>
              </Space>
            </Col>
          </Row>
        </Col>

        {/* CPM and Impressions Row - shown for CPM pricing model */}
        {!hideCpmFields && (
          <Col span={24}>
            <Row gutter={16}>
              {/* Campaign CPM */}
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text strong>CPM</Text>
                  <Text type="secondary">Rate per 1,000 impressions</Text>
                  <Tooltip title={!canEditCpm ? "Only Ad Ops users can edit CPM" : ""}>
                    <Form.Item
                      name={[campaignIndex, 'cpm']}
                      fieldKey={[campaignIndex, 'cpm']}
                      rules={[
                        {
                          required: !hideCpmFields,
                          message: 'CPM is required',
                        },
                        {
                          validator: async (_, value) => {
                            // Skip validation for Percentage pricing model
                            if (hideCpmFields) {
                              return Promise.resolve();
                            }

                            // Allow empty values (required rule handles this)
                            if (value === '' || value === null || value === undefined) {
                              return Promise.resolve();
                            }

                            // Check if advertiser is selected
                            if (!selectedAdvertiser) {
                              throw new Error('Please select an advertiser first');
                            }

                            // Get minimum CPM from advertiser for this campaign type
                            const minimumCpm = getMinimumCpmForCampaignType(selectedAdvertiser, campaignType);

                            // Validate against minimum
                            if (value < minimumCpm) {
                              const campaignTypeLabel = campaignType === 1 ? 'Display' : campaignType === 7 ? 'External' : campaignType === 8 ? 'Video' : 'this campaign type';
                              throw new Error(`CPM must be at least $${minimumCpm.toFixed(2)} (advertiser minimum for ${campaignTypeLabel})`);
                            }

                            return Promise.resolve();
                          },
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="7.00"
                        min={0}
                        max={Math.pow(10, 12) - 1}
                        precision={2}
                        disabled={!canEditCpm}
                        formatter={value => {
                          if (!value && value !== 0) return '';
                          return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                        }}
                        parser={value => value ? value.replace(/\$\s?|(,*)/g, '') : undefined}
                      />
                    </Form.Item>
                  </Tooltip>
                </Space>
              </Col>

              {/* Campaign Impressions (calculated) */}
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text strong>Impressions</Text>
                  <Text type="secondary">Campaign total</Text>
                  <Input
                    style={{ width: '100%' }}
                    disabled
                    value={(campaignImpressions || 0).toLocaleString()}
                  />
                </Space>
              </Col>
            </Row>
          </Col>
        )}

        {/* Pricing Row - shown for Percentage pricing model */}
        {hideCpmFields && (
          <Col span={24}>
            <Row gutter={16}>
              {/* Auction Plus Percentage Field */}
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text strong>Auction Plus Percentage</Text>
                  <Text type="secondary">Override auction plus %</Text>
                  <Tooltip title={!canEditCpm ? "Only Ad Ops users can edit percentage" : ""}>
                    <Form.Item
                      name={[campaignIndex, 'advertiserPercentage']}
                      fieldKey={[campaignIndex, 'advertiserPercentage']}
                      rules={[
                        { required: true, message: 'Auction Plus Percentage is required' },
                        {
                          validator: (_, value) => validatePercentageOverride(value, selectedAdvertiser)
                        }
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder={selectedAdvertiser ? `${getPercentageForAdvertiser(selectedAdvertiser) || 0}` : '0'}
                        min={0}
                        max={99.99}
                        precision={2}
                        disabled={!canEditCpm}
                        suffix="%"
                      />
                    </Form.Item>
                  </Tooltip>
                </Space>
              </Col>
            </Row>
          </Col>
        )}

        {/* Budget Warning - appears right after budget row - "No line items marked for delivery" only in advanced mode */}
        {showBudgetWarning && budgetValidation.valid && budgetValidation.warning && (isAdvancedMode || budgetValidation.warning !== 'No line items marked for delivery') && (
          <Col span={24}>
            <Alert
              type="warning"
              message="Budget Warning"
              description={budgetValidation.warning}
              showIcon
              style={{ marginBottom: 0 }}
            />
          </Col>
        )}

        {/* Media Plan Strategy Toggle */}
        <Col span={24}>
          <Row gutter={16}>
            <Col span={24}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Space align="center">
                  <Text strong>Has Media Plan Strategy</Text>
                  <Form.Item
                    name={[campaignIndex, 'hasMediaPlanStrategy']}
                    fieldKey={[campaignIndex, 'hasMediaPlanStrategy']}
                    valuePropName="checked"
                    style={{ marginBottom: 0 }}
                  >
                    <Switch />
                  </Form.Item>
                </Space>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Enable this to indicate that Ad Ops will handle the strategic media planning.
                  Line items become optional when enabled.
                </Text>
                {hasMediaPlanStrategy && (
                  <Alert
                    message="Media Plan Strategy Enabled"
                    description="Line items are now optional. You can add line items as guidance for Ad Ops, or leave them empty for Ad Ops to create."
                    type="info"
                    showIcon
                  />
                )}
              </Space>
            </Col>
          </Row>
        </Col>

        {/* DSP Configuration - Advanced Mode Only */}
        {isAdvancedMode && (
          <Col span={24}>
            <Row gutter={16}>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Space>
                    <Text strong>Demand-Side Platform</Text>
                    <Tooltip title="Select the DSP for programmatic launching of this campaign">
                      <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                    </Tooltip>
                  </Space>
                  <Form.Item
                    name={[campaignIndex, 'demandSidePlatform']}
                    fieldKey={[campaignIndex, 'demandSidePlatform']}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Select DSP"
                      allowClear
                      options={[
                        { value: 'NEXXEN', label: 'NEXXEN' },
                        { value: 'LIQUIDM', label: 'LIQUIDM' },
                        { value: 'JAMLOOP', label: 'JAMLOOP' },
                      ]}
                    />
                  </Form.Item>
                </Space>
              </Col>
              <Col span={6}>
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Space>
                    <Text strong>Ad Provider ID</Text>
                    <Tooltip title="Unique identifier for the ad provider">
                      <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                    </Tooltip>
                  </Space>
                  <Form.Item
                    name={[campaignIndex, 'adProviderId']}
                    fieldKey={[campaignIndex, 'adProviderId']}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      style={{ width: '100%' }}
                      placeholder="Enter Ad Provider ID"
                    />
                  </Form.Item>
                </Space>
              </Col>
              {!hideCpmFields && (
                <Col span={6}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Space>
                      <Text strong>Maximum Bid *</Text>
                      <Tooltip title="Maximum bid amount for the campaign (required for CPM pricing)">
                        <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                      </Tooltip>
                    </Space>
                    <Form.Item
                      name={[campaignIndex, 'maxBid']}
                      fieldKey={[campaignIndex, 'maxBid']}
                      rules={[
                        {
                          required: true,
                          message: '',
                        },
                        {
                          type: 'number',
                          min: 0.01,
                          message: '',
                          transform: (value) => {
                            const num = parseFloat(value);
                            return isNaN(num) ? undefined : num;
                          },
                        },
                      ]}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        placeholder="4.00"
                        prefix="$"
                        min={0.01}
                        step={0.01}
                        precision={2}
                      />
                    </Form.Item>
                  </Space>
                </Col>
              )}
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Space align="center">
                  <Switch
                    checked={showOnlyDeliveryItems}
                    onChange={onDeliveryFilterChange}
                  />
                  <Text strong>Show Delivery Items Only</Text>
                  <Tooltip title="Filter to display only line items marked for delivery">
                    <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                  </Tooltip>
                </Space>
              </Col>
            </Row>
          </Col>
        )}

        {/* Campaign-Level Flight Schedule (Advanced Mode + NEXXEN DSP + non-DRAFT + Ad Ops) */}
        {isAdvancedMode && campaignDsp === 'NEXXEN' && isNotDraftStatus && hasTraffickerOrProcessorPermission && (
          <Col span={24}>
            <Card title="CAMPAIGN FLIGHT SCHEDULE" style={{ marginTop: 8 }}>
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {/* Flight Overlap Warning */}
                {flightOverlaps.length > 0 && (
                  <Alert
                    type="error"
                    message="Overlapping Flight Dates Detected"
                    description={
                      <div>
                        <p>The following flights have overlapping date ranges:</p>
                        <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                          {flightOverlaps.map((overlap, idx) => (
                            <li key={idx}>
                              Flight {overlap.flightIndex1 + 1} and Flight {overlap.flightIndex2 + 1}
                            </li>
                          ))}
                        </ul>
                        <p style={{ marginTop: 8, marginBottom: 0 }}>
                          Please adjust the date ranges to ensure flights do not overlap.
                        </p>
                      </div>
                    }
                    showIcon
                  />
                )}
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Configure multiple flight date ranges for phased campaign execution (maps to Nexxen IO schedules)
                    </Text>
                  </div>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      let newStartDate, newEndDate;

                      if (flightSchedule.length === 0) {
                        // First flight: use campaign dates (already in UTC from timezone-aware pickers)
                        newStartDate = campaignStartDate
                          ? moment(campaignStartDate)
                          : (orderDates.startDate ? moment(orderDates.startDate) : moment.tz(timezone).startOf('day').utc());
                        newEndDate = campaignEndDate
                          ? moment(campaignEndDate)
                          : (orderDates.endDate ? moment(orderDates.endDate) : moment.tz(timezone).add(2, 'days').endOf('day').utc());
                      } else {
                        // Subsequent flights: start after the furthest end date
                        let furthestEndDate = null;
                        flightSchedule.forEach((flight) => {
                          if (flight.endDate) {
                            const flightEnd = moment(flight.endDate);
                            if (!furthestEndDate || flightEnd.isAfter(furthestEndDate)) {
                              furthestEndDate = flightEnd;
                            }
                          }
                        });

                        if (!furthestEndDate) {
                          furthestEndDate = campaignEndDate ? moment(campaignEndDate) : moment.tz(timezone);
                        }

                        // Convert to user timezone to get the calendar date, then add 1 day, convert back to UTC
                        const nextDay = moment(furthestEndDate).tz(timezone).add(1, 'day').format('YYYY-MM-DD');
                        newStartDate = moment.tz(nextDay, timezone).startOf('day').utc();
                        newEndDate = moment.tz(nextDay, timezone).add(2, 'days').endOf('day').utc();
                      }

                      const newFlight = {
                        startDate: newStartDate,
                        endDate: newEndDate,
                        budget: null,
                      };

                      // Only add CPM for CPM pricing model
                      if (!hideCpmFields) {
                        newFlight.cpm = campaignCpm || 7;
                      }

                      const updatedFlightSchedule = [...flightSchedule, newFlight];
                      setFlightSchedule(updatedFlightSchedule);

                      // Auto-adjust end dates if the new flight's end date exceeds them
                      autoAdjustEndDatesFromFlightSchedule(updatedFlightSchedule);
                    }}
                  >
                    Add Flight
                  </Button>
                </Space>

                <Table
                  dataSource={flightSchedule.map((flight, idx) => {
                    const numberOfDays = flight.startDate && flight.endDate
                      ? moment(flight.endDate).diff(moment(flight.startDate), 'days') + 1
                      : null;
                    const impressionGoal = flight.budget && flight.cpm
                      ? Math.round((flight.budget / flight.cpm) * 1000)
                      : null;
                    return {
                      key: idx,
                      ...flight,
                      numberOfDays,
                      impressionGoal,
                    };
                  })}
                  columns={[
                    {
                      title: <span>Start Date <Text type="danger">*</Text></span>,
                      dataIndex: 'startDate',
                      key: 'startDate',
                      width: 150,
                      render: (value, _, idx) => (
                        <DatePicker
                          style={{
                            width: '100%',
                            borderColor: !value ? '#ff4d4f' : undefined
                          }}
                          value={value ? moment(value).tz(timezone) : null}
                          format="MM/DD/YYYY"
                          placeholder="Start date"
                          status={!value ? 'error' : undefined}
                          onChange={(date) => {
                            const updated = [...flightSchedule];
                            const normalizedDate = date ? moment.tz(date.format('YYYY-MM-DD'), timezone).startOf('day').utc() : null;
                            updated[idx] = { ...updated[idx], startDate: normalizedDate };
                            setFlightSchedule(updated);
                          }}
                        />
                      ),
                    },
                    {
                      title: <span>End Date <Text type="danger">*</Text></span>,
                      dataIndex: 'endDate',
                      key: 'endDate',
                      width: 150,
                      render: (value, _, idx) => (
                        <DatePicker
                          style={{
                            width: '100%',
                            borderColor: !value ? '#ff4d4f' : undefined
                          }}
                          value={value ? moment(value).tz(timezone) : null}
                          format="MM/DD/YYYY"
                          placeholder="End date"
                          status={!value ? 'error' : undefined}
                          onChange={(date) => {
                            const updated = [...flightSchedule];
                            const normalizedDate = date ? moment.tz(date.format('YYYY-MM-DD'), timezone).endOf('day').utc() : null;
                            updated[idx] = { ...updated[idx], endDate: normalizedDate };
                            setFlightSchedule(updated);
                            autoAdjustEndDatesFromFlightSchedule(updated);
                          }}
                        />
                      ),
                    },
                    {
                      title: <span>Budget <Text type="danger">*</Text></span>,
                      dataIndex: 'budget',
                      key: 'budget',
                      width: 150,
                      render: (value, _, idx) => (
                        <InputNumber
                          style={{
                            width: '100%',
                            borderColor: (!value || value <= 0) ? '#ff4d4f' : undefined
                          }}
                          value={value}
                          placeholder="500"
                          prefix="$"
                          min={0}
                          precision={2}
                          status={(!value || value <= 0) ? 'error' : undefined}
                          formatter={val => val ? `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                          parser={val => val.replace(/\$\s?|(,*)/g, '')}
                          onChange={(newValue) => {
                            const updated = [...flightSchedule];
                            updated[idx] = { ...updated[idx], budget: newValue };
                            setFlightSchedule(updated);
                          }}
                        />
                      ),
                    },
                    // CPM column - hidden for Percentage pricing
                    ...(!hideCpmFields ? [{
                      title: <span>CPM <Text type="danger">*</Text></span>,
                      dataIndex: 'cpm',
                      key: 'cpm',
                      width: 120,
                      render: (value, _, idx) => (
                        <InputNumber
                          style={{
                            width: '100%',
                            borderColor: (!value || value < 1) ? '#ff4d4f' : undefined
                          }}
                          value={value}
                          placeholder="10"
                          min={0}
                          precision={2}
                          status={(!value || value < 1) ? 'error' : undefined}
                          onChange={(newValue) => {
                            const updated = [...flightSchedule];
                            updated[idx] = { ...updated[idx], cpm: newValue };
                            setFlightSchedule(updated);
                          }}
                        />
                      ),
                    }] : []),
                    // Impressions column - hidden for Percentage pricing
                    ...(!hideCpmFields ? [{
                      title: 'Impressions',
                      dataIndex: 'impressionGoal',
                      key: 'impressionGoal',
                      width: 120,
                      align: 'right',
                      render: (value) => (
                        <Text>{value ? value.toLocaleString() : '-'}</Text>
                      ),
                    }] : []),
                    {
                      title: 'Days',
                      dataIndex: 'numberOfDays',
                      key: 'numberOfDays',
                      width: 80,
                      align: 'center',
                      render: (value) => (
                        <Text>{value !== null ? value : '-'}</Text>
                      ),
                    },
                    {
                      title: '',
                      key: 'actions',
                      width: 50,
                      align: 'center',
                      render: (_, __, idx) => (
                        <Button
                          danger
                          size="small"
                          type="text"
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            const updated = flightSchedule.filter((_, i) => i !== idx);
                            setFlightSchedule(updated);
                          }}
                        />
                      ),
                    },
                  ]}
                  pagination={false}
                  size="small"
                  locale={{
                    emptyText: 'No campaign flight dates configured. Click "Add Flight" to add a new flight.',
                  }}
                />
              </Space>
            </Card>
          </Col>
        )}

        {/* Validation Alerts */}
        {!dateValidation.valid && dateValidation.error && (
          <Col span={24}>
            <Alert
              type="error"
              message="Date Range Error"
              description={dateValidation.error}
              showIcon
              style={{ marginBottom: 0 }}
            />
          </Col>
        )}

        {!budgetValidation.valid && budgetValidation.error && (
          <Col span={24}>
            <Alert
              type="error"
              message="Budget Allocation Error"
              description={budgetValidation.error}
              showIcon
              style={{ marginBottom: 0 }}
            />
          </Col>
        )}
      </Row>
    </Card>
  );
});

CampaignHeader.displayName = 'CampaignHeader';

export default CampaignHeader;
